import { useDocumentTitle } from "@uidotdev/usehooks";
import { Navigate, NavLink, useNavigate, useParams } from "react-router-dom";

import AppointmentCard from "@/components/AppointmentCard";
import Button from "@/components/Button";
import ErrorText from "@/components/ErrorText";
import Header from "@/components/Header";
import Link from "@/components/Link";
import Loader from "@/components/Loader";
import { ExternalLinks } from "@/constants/links";
import { ErrorMessages } from "@/constants/messages";
import { PageRoute, PageTitle } from "@/constants/pages";
import { useAppointmentQuery } from "@/hooks/appointments";
import { usePageEventTracking } from "@/hooks/event-tracking";
import { useCancelAppointmentMutation } from "@/hooks/stores";

export default function AppointmentCancelPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const appointmentQuery = useAppointmentQuery(id);

  const _appointment =
    appointmentQuery.data?.type === "success"
      ? appointmentQuery.data.appointment
      : null;

  const cancelAppointmentMutation = useCancelAppointmentMutation();

  usePageEventTracking({
    service_name: _appointment?.service_description,
    service_date: _appointment?.start_datetime,
  });

  useDocumentTitle(
    PageTitle.APPOINTMENT_CANCEL(_appointment?.service_description ?? ""),
  );

  if (!id) {
    return <Navigate to={PageRoute.HOME} />;
  }

  if (appointmentQuery.isPending) {
    return <Loader />;
  }

  if (appointmentQuery.isError || appointmentQuery.data.type === "error") {
    return <ErrorText error={ErrorMessages.GENERIC} center />;
  }

  if (appointmentQuery.data.type === "notCustomersAppointment") {
    return <ErrorText error={ErrorMessages.INCORRECT_ACCOUNT} center />;
  }

  const { appointment } = appointmentQuery.data;

  return (
    <>
      <Header className="mb-6 text-sunset-orange">
        Are you sure you want to cancel?
      </Header>

      <AppointmentCard className="mb-8" appointment={appointment} />

      <p className="mb-2 font-semibold">Please note:</p>

      <p className="mb-8">
        If this cancellation occurs within 24 hours of your appointment start
        time, a late cancellation fee may apply. Visit{" "}
        <Link href={ExternalLinks.TERMS} target="_blank">
          restore.com/terms
        </Link>{" "}
        for information.
      </p>

      {cancelAppointmentMutation.isPending ? (
        <Loader />
      ) : (
        <div className="mb-4 flex flex-col gap-4">
          <Button
            type="button"
            color="red"
            onClick={() =>
              cancelAppointmentMutation.mutate(id, {
                onSuccess: () => {
                  navigate(PageRoute.APPOINTMENT_CANCELED(id ?? ""));
                },
              })
            }
          >
            Confirm cancellation
          </Button>
          <Button as={NavLink} to={PageRoute.APPOINTMENT(id)} color="blue">
            Keep appointment
          </Button>
        </div>
      )}
    </>
  );
}
